import {
	OperationalContactOrigin,
	OperationalLastContact,
} from "../../../store/ducks/operationalDashboard";
import { v4 as uuidv4 } from "uuid";

export interface RegisterWhatsAppContactRequest {
	ID_USUARIO: number;
	ORIGEM: OperationalContactOrigin;
}

export interface RegisteredWhatsAppContact {
	id: number;
	patientId: number;
	actorId: number;
	actorName: string | null;
	origin: OperationalContactOrigin;
	channel: "WHATSAPP";
	event: "CONTACT_STARTED";
	occurredAt: string;
}

export class PatientContactApiError extends Error {
	status: number;

	constructor(status: number, message: string) {
		super(message);
		this.name = "PatientContactApiError";
		this.status = status;
	}
}

const isRegisteredContact = (value: any): value is RegisteredWhatsAppContact =>
	Number.isSafeInteger(value?.id) &&
	Number.isSafeInteger(value?.patientId) &&
	Number.isSafeInteger(value?.actorId) &&
	(value?.actorName === null || typeof value?.actorName === "string") &&
	(value?.origin === "REEVALUATION" || value?.origin === "CRITICAL_ALERT") &&
	value?.channel === "WHATSAPP" &&
	value?.event === "CONTACT_STARTED" &&
	typeof value?.occurredAt === "string" &&
	!Number.isNaN(Date.parse(value.occurredAt));

export const createIdempotencyKey = (): string => {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	return uuidv4();
};

export const toOperationalLastContact = (
	contact: RegisteredWhatsAppContact
): OperationalLastContact => ({
	ID_CONTATO: contact.id,
	ID_AVALIADOR: contact.actorId,
	AVALIADOR_NOME: contact.actorName,
	CANAL: contact.channel,
	EVENTO: contact.event,
	ORIGEM: contact.origin,
	DATA_HORA: contact.occurredAt,
});

export const registerWhatsAppContact = async (
	token: string,
	payload: RegisterWhatsAppContactRequest,
	idempotencyKey: string,
	fetchImpl: typeof fetch = fetch
): Promise<RegisteredWhatsAppContact> => {
	const response = await fetchImpl(
		`${process.env.REACT_APP_BASE_URL_V7}dashboard/operacional/paciente/contatos`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
				"Idempotency-Key": idempotencyKey,
			},
			body: JSON.stringify(payload),
		}
	);

	let body: any = null;
	try {
		body = await response.json();
	} catch (_) {
		body = null;
	}

	if (
		response.status !== 200 ||
		body?.status !== "success" ||
		body?.code !== 0 ||
		!isRegisteredContact(body?.contato)
	) {
		throw new PatientContactApiError(
			response.status,
			typeof body?.message === "string"
				? body.message
				: "Não foi possível registrar o contato."
		);
	}
	if (
		body.contato.patientId !== payload.ID_USUARIO ||
		body.contato.origin !== payload.ORIGEM
	) {
		throw new PatientContactApiError(
			200,
			"O servidor retornou um contato diferente do solicitado."
		);
	}

	return body.contato;
};
